/**
 * Inline row inside the publish dialog's share-link card: a link for one viewer.
 *
 * Closed (default): one line, "Track who watches". Open: an id field and a Copy link button.
 * The link is the share link with `?actor=<id>`; Analytics then shows one row per id. The id
 * sits in the url, so the row refuses an email and anything outside letters, digits, . _ -.
 */

import { useState } from 'react';
import { Check, Copy, Users } from 'lucide-react';
import { viewerIdProblem, viewerLink } from '@/lib/vplay';

interface Props {
  url: string;
  linkKey: string;
  onCopy: (link: string) => void;
  copied: boolean;
}

export function ViewerLinkRow({ url, linkKey, onCopy, copied }: Props) {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState('');
  const trimmed = id.trim();
  const problem = trimmed ? viewerIdProblem(trimmed) : null;
  const link = trimmed && !problem ? viewerLink(url, trimmed) : null;
  const inputId = `viewer-id-${linkKey}`;

  return (
    <div className="mb-1.5 px-2 py-1.5 rounded border text-[11px] bg-muted/30 border-border/50">
      <button
        type="button"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className="flex items-start gap-2 w-full text-left"
      >
        <Users className="mt-0.5 h-3 w-3 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-foreground">Track who watches</span>
          <p className="text-muted-foreground mt-0.5 leading-snug">
            Give each viewer their own link; Analytics shows a row per person.
          </p>
        </div>
      </button>

      {open && (
        <div className="mt-2 pl-5 space-y-1.5">
          <label htmlFor={inputId} className="block text-muted-foreground leading-snug">
            Viewer id: something you can look up later, like a student number. Not a name or email: it is part of the link.
          </label>
          <div className="flex items-center gap-1.5">
            <input
              id={inputId}
              value={id}
              placeholder="e.g. s-014"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!problem}
              onChange={(e) => setId(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-0 bg-background/60 rounded border border-border/50 px-2 py-1 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              disabled={!link}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (link) onCopy(link);
              }}
              className="h-6 px-2 rounded text-[10px] font-medium inline-flex items-center gap-1 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
          {problem && <p role="alert" className="text-destructive leading-snug">{problem}</p>}
          {link && (
            <code className="block bg-background/60 rounded px-2 py-1 text-[10px] truncate font-mono text-muted-foreground">
              {link}
            </code>
          )}
        </div>
      )}
    </div>
  );
}
